<template>
  <v-card class="pa-1 ma-2">
    <ScopeComponent @onScopeChange="search" />
    <!-- <v-card-title>{{ $t("titles.courses") }}</v-card-title> -->
    <v-card-title class="d-flex flex-gap justify-space-between flex-column flex-sm-row pb-2">
      <h3>{{ $t("titles.courses") }}</h3>

      <div class="btn-container">
        <v-btn
          v-if="userRights?.includes('9.3.') && showAddBtn"
          id="coursesList-btn-add"
          dark
          class="success"
          :to="{ name: addUrl }"
        >{{ $t("titles.addCourse") }}</v-btn
        >

        <v-btn
          v-if="userRights?.includes('9.12.') && showAddBtn"
          id="coursesList-btn-importInstructors"
          dark
          class="success--text"
          variant="outlined"
          :to="{ name: instructorsUrl }"
        >{{ $t("titles.ImportInstructors") }}</v-btn
        >
      </div>
    </v-card-title>

    <v-divider class="ma-2" />

    <v-card-text>
      <CoursesListFiltersComponent
        @search="search"
        v-model="filtersInputs"
        :value="filtersInputs"
        :filtersData="filtersOptions"
        :loading="loadingFilters || loading"
      />
    </v-card-text>
    <v-divider class="ma-2" />

    <v-card-actions
      class="d-flex flex-wrap justify-end align-center px-2 mb-2 actions-container"
    >
      <v-btn
        id="coursesList-btn-search"
        class="ma-1 white--text"
        color="primary"
        variant="flat"
        :loading="loading"
        :disabled="loadingFilters || loading "
        @click="search"
      >{{ $t("buttons.search") }}</v-btn
      >
      <v-btn
        id="coursesList-btn-reset"
        class="ma-1"
        color="secondary"
        variant="flat"
        :disabled="loadingFilters || loading"
        @click="resetSearch"
      >{{ $t("buttons.reset") }}</v-btn
      >
    </v-card-actions>
    <v-card-text>
      <v-data-table-server
        class="clickable"
        :headers="headers"
        :items="courseList"
        :items-length="totalItems"
        :loading-text="$t('labels.loading')"
        :no-data-text="$t('labels.noData')"
        :loading="loading"
        v-model:page="tablePage"
        v-model:items-per-page="tableLimit"
        :items-per-page-options="limitOption"
        :items-per-page-text="$t('RowsPerPage')"
        mobile-breakpoint="sm"
        @update:options="onTableOptionsUpdate"
        @click:row="(event, { item }) => openCourseSubjectList(item)"
      >
        <template v-slot:loading>
          {{ $t('labels.loading') }}
        </template>

        <template v-slot:no-data>
          {{ $t('labels.noData') }}
        </template>

        <template v-slot:item.index="{ index }">
          {{ (tablePage - 1) * tableLimit + 1 + index }}
        </template>

        <template v-slot:item.courseName="{ item }">
          {{ $utils.trim($utils.servertranslate(item.courseName)) }}
        </template>

        <template v-slot:item.studyPlan="{ item }">
          {{ $utils.trim($utils.servertranslate(item.studyPlan)) }}
        </template>

        <template v-slot:item.courseType="{ item }">
          {{ $utils.servertranslate((item.courseType as { baName: string }).baName) }}
        </template>

        <template v-slot:item.actions="{ item }">
          <div
            v-if="showAction && item.isEditable"
            class="d-flex justify-center align-center"
          >
            <v-switch
              id="coursesList-btn-updateStatus"
              color="success"
              class="mx-2"
              hide-details
              v-model="item.active"
              @click.stop="showActivation(item)"
              :title="$t(`labels.${item.active ? 'active' : 'inactive'}`)"
            />
          </div>
        </template>
      </v-data-table-server>
    </v-card-text>

    <DialogComponent @close="cancel" v-if="showDialog" v-model="showDialog" :value="showDialog">
      <template v-slot:header>
        {{ $t("titles.confirmMessage") }}
      </template>
      <template v-slot:body>
        <h3 style="font-size: 18px">
          {{ $t(active ? "courseDeactivateConfirm" : "courseActivateConfirm") }}
        </h3>
      </template>
      <template v-slot:actions>
        <v-btn
          id="courseActivateDialog-btn-cancel"
          class="mx-1 secondary white--text"
          @click="cancel"
        >
          {{ $t("buttons.cancel") }}
        </v-btn>

        <v-btn
          id="courseActivateDialog-btn-confirm"
          class="mx-1 success"
          @click="activate()"
        >
          {{ $t("buttons.confirm") }}
        </v-btn>
      </template>
    </DialogComponent>
    <CourseActivationDialog
      v-if="subjectsToBeActivated"
      :subjectsToBeActivated="subjectsToBeActivated"
      :active="active"
      :courseId="String(courseId)"
      @close="closeModal(active)"
      @activationDone="closeModal(!active)"
    />
  </v-card>
</template>

<script lang='ts'>
import { defineComponent } from 'vue';
import CoursesListFiltersComponent from '@/components/filters/coursesListFilters.component.vue';
import { Subject } from '@/types/subject.types';
/* eslint-disable import/no-cycle */
import CourseListMixin from '@/mixins/courseList.mixin';
import ScopeComponent from '@/components/scope.component.vue';
import DialogComponent from '@/components/dialog.component.vue';
import CourseActivationDialog from '@/components/courses/coursesActivation.dialog.vue';
import { COURSES_ROUTE_NAME } from '@/enums';
import { useToaster } from '@/composables/useToaster';

export default defineComponent({
  name: 'SharedCourseList',
  mixins: [CourseListMixin],
  setup() {
    return {
      ...useToaster(),
    };
  },
  emits: ['rowClicked'],
  components: {
    CoursesListFiltersComponent,
    ScopeComponent,
    DialogComponent,
    CourseActivationDialog,
  },
  data() {
    return {
      title: 'Test Component',
      addUrl: COURSES_ROUTE_NAME.ADD,
      instructorsUrl: COURSES_ROUTE_NAME.INSTRUCTORS,
      subjects: [] as Subject[],
      selectedSubjects: [] as string[],
      id: 0,
      courseId: '' as string | number,
      showDialog: false,
      subjectsToBeActivated: false,
      active: false,
    };
  },
  computed: {
    headers(): any {
      const headersArray = [
        {
          title: this.$t('tableHeaders.index'),
          sortable: false,
          align: 'center',
          key: 'index',
          class: this.headerClass,
        },
        {
          title: this.$t('tableHeaders.courseName'),
          sortable: true,
          key: 'courseName',
          align: 'center',
          class: this.headerClass,
        },
        {
          title: this.$t('tableHeaders.studyPlan'),
          sortable: true,
          key: 'studyPlan',
          align: 'center',
          class: this.headerClass,
        },
        {
          title: this.$t('tableHeaders.courseType'),
          sortable: false,
          align: 'center',
          key: 'courseType',
          class: this.headerClass,
        },
      ];
      if (this.showAction === true) {
        headersArray.push({
          title: '',
          sortable: false,
          align: 'center',
          key: 'actions',
          class: this.headerClass,
        });
      }
      return headersArray;
    },
  },
  methods: {
    onTableOptionsUpdate(options: { page: number, itemsPerPage: number, sortBy: any[] }) {
      this.tablePage = options.page;
      this.tableLimit = options.itemsPerPage;
      this.sortKeys = options.sortBy;

      // Sync API params
      this.page = options.page;
      this.limit = options.itemsPerPage;

      // Trigger API fetch
      this.getCoursesList();
    },
    async customOptionUpdate(options) {
      // await this.onOptionUpdate(options);
      if (this.$store.state.course.selectedCourseId && this.$store.state.course.selectedCourseId !== '') {
        this.openCourseSubjectList({ courseId: this.$store.state.course.selectedCourseId, courseName: this.$store.state.course.selectedCourseName });
      }
    },
    onCourseClick(courseId: string) {
      this.$router.push({
        name: 'subject-list',
        params: { courseId },
      });
    },
    openCourseSubjectList(course) {
      this.$store.commit('selectCourse', {
        course,
      });
      this.$emit('rowClicked', {
        queries: this.queries,
        courseId: course.courseId,
      });
    },
    showActivation(course) {
      this.courseId = course.courseId;
      this.active = course.active;
      this.showDialog = true;
    },
    async activate() {
      this.showDialog = false;
      this.subjectsToBeActivated = true;
    },
    closeModal(active) {
      this.refreshTable(active);
      this.subjectsToBeActivated = false;
    },
    refreshTable(active) {
      const index = this.courseList.findIndex(
        (c) => c.courseId === this.courseId,
      );
      const item = { ...this.courseList[index], active };
      this.courseList = [
        ...this.courseList.slice(0, index),
        item,
        ...this.courseList.slice(index + 1),
      ];
    },
    cancel() {
      this.refreshTable(this.active);
      this.showDialog = false;
    },
  },
});
</script>

<style lang="scss" scoped>

td{
  text-align: center;
}
:deep(.v-data-table__thead) {
  background-color: #212121;
  font-weight: bold;
}
:deep(#coursesList-btn-updateStatus-messages){
  display: none;
}
.actions-container {
  @media (max-width: 576px) {
    flex-flow: column !important;
    .v-btn {
      width: 100% !important;
    }
  }
}
.flex-gap {
  gap: 8px;
}
.btn-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  @media screen and (min-width: 500px) {
    height: 32px;
  }
}
</style>
